/**
 * Impression et export PDF de l'aperçu de facture affiché à l'écran.
 *
 * Extraits de CreateInvoice.tsx sans changement de comportement. Ces deux fonctions travaillent sur
 * l'élément du DOM qui porte l'aperçu (`.invoice-print-area`) : l'une l'envoie dans une fenêtre
 * d'impression, l'autre le capture en image puis le découpe page par page dans un PDF A4.
 *
 * À ne pas confondre avec invoicePdfGenerator.ts, qui reconstruit la facture en HTML depuis les
 * données enregistrées — c'est lui qui sert à l'historique, quand aucun aperçu n'est à l'écran.
 */
import { toPng } from 'html-to-image';

/** Feuille de style appliquée au document envoyé à l'imprimante. */
const PRINT_STYLES = `
body { margin: 0; padding: 0; background: #ffffff; }
.print-hidden { display: none !important; }
.invoice-print-area {
  box-sizing: border-box !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: none !important;
  border: none !important;
  max-width: none !important;
  width: 100% !important;
  margin: 0 auto !important;
  position: static !important;
  overflow: visible !important;
  min-height: calc(297mm - 6mm) !important;
  padding: 3mm !important;
}
.invoice-items-table { page-break-inside: auto; }
.invoice-scroll { overflow: visible !important; }
.invoice-items-table { min-width: 0 !important; }
.invoice-items-table thead { display: table-header-group; }
.invoice-line-row { break-inside: avoid; page-break-inside: avoid; }
.invoice-bottom-block {
  margin-top: auto !important;
}
.invoice-summary-section,
.invoice-legal-footer,
.invoice-keep-together,
.invoice-bottom-block {
  break-inside: avoid-page;
  page-break-inside: avoid;
}
.invoice-summary-section { margin-top: 3mm !important; }
.invoice-legal-footer { margin-top: 3mm !important; padding-top: 2mm !important; }
`;

/**
 * Ouvre la fenêtre d'impression sur l'aperçu. `targetWindow` permet de passer une fenêtre déjà
 * ouverte : les navigateurs bloquent un window.open() déclenché après un await.
 */
export const openInvoicePrintWindow = (
    element: HTMLElement | null,
    invoiceNumber: string,
    targetWindow?: Window | null,
) => {
    const popup = targetWindow ?? window.open('', '_blank', 'width=900,height=1200');
    if (!popup) {
        alert('Veuillez autoriser la fenêtre d’impression.');
        return;
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) => node.outerHTML)
        .join('\n');
    const content = element?.outerHTML || '';

    popup.document.open();
    popup.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${invoiceNumber || 'Facture'}</title>
${styles}
<style>${PRINT_STYLES}</style>
</head>
<body>${content}</body>
</html>`);
    popup.document.close();
    popup.focus();
    window.setTimeout(() => popup.print(), 250);
};

/**
 * Capture l'aperçu en image puis le découpe en pages A4, sans jamais couper une ligne d'article,
 * un paragraphe ou un titre en deux entre deux pages.
 */
export const generateInvoicePdfFromElement = async (element: HTMLElement | null) => {
    if (!element) {
        throw new Error('Aperçu de facture introuvable.');
    }
    const source = element;
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.width = '210mm';
    wrapper.style.background = '#ffffff';
    wrapper.style.padding = '0';
    wrapper.style.margin = '0';
    wrapper.style.zIndex = '-1';

    const clone = source.cloneNode(true) as HTMLDivElement;
    clone.style.width = '210mm';
    clone.style.maxWidth = '210mm';
    // Écrase le min-h-[294mm] de la classe Tailwind d'origine (utile pour l'aperçu à l'écran) : sur un
    // document multi-page, imposer une hauteur minimale de ~1 page dépasse légèrement le budget réel de
    // la page 1 (297mm - marges) et force à tort TOUT document, même très court, à déborder sur une 2e
    // page — avec les protections anti-coupure, cela pousse le tableau entier en page 2 plutôt que de le
    // couper, laissant la page 1 quasi vide (bug remonté par Emeni sur le devis avec le même mécanisme,
    // screenshot à l'appui : « les lignes de prestation ne commencent qu'à la page 2 »).
    clone.style.minHeight = '0';
    clone.style.margin = '0';
    // Marge de 8mm sur les 4 côtés — même convention que le devis Prestation et le contrat
    // d'abonnement (ni trop étroite comme les 3mm précédents, ni trop large).
    clone.style.padding = '8mm';
    clone.style.boxSizing = 'border-box';
    clone.style.border = 'none';
    clone.style.boxShadow = 'none';
    clone.style.borderRadius = '0';
    clone.style.background = '#ffffff';
    clone.style.overflow = 'hidden';
    // Neutralise le mt-auto (Tailwind) du bloc total/pied de page — pertinent seulement quand le
    // conteneur a une hauteur minimale forcée (ce qui n'est plus le cas ci-dessus) ; sinon il ne fait
    // rien de mal, mais autant l'expliciter pour ne jamais dépendre d'un espace flex résiduel imprévisible.
    const bottomBlock = clone.querySelector<HTMLElement>('.invoice-bottom-block');
    if (bottomBlock) bottomBlock.style.marginTop = '16px';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    // Convertir toutes les <img> du clone en data URI pour éviter les erreurs CORS/404
    // que html-to-image lève comme Event (et non Error)
    const allImgs = clone.querySelectorAll('img');
    await Promise.all(Array.from(allImgs).map(async (img) => {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) return; // déjà data URI, rien à faire
        try {
            const resp = await fetch(src, { mode: 'cors', cache: 'force-cache' });
            if (!resp.ok) throw new Error('fetch failed');
            const blob = await resp.blob();
            const dataUri = await new Promise<string>((res, rej) => {
                const reader = new FileReader();
                reader.onload = () => res(reader.result as string);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
            });
            img.src = dataUri;
        } catch {
            // Image inaccessible : la masquer pour ne pas bloquer toPng
            img.style.display = 'none';
        }
    }));

    // Attend que chaque image (notamment le logo, déjà en data URI la plupart du temps) ait fini de
    // décoder avant de fixer sa taille explicite ci-dessous — sinon naturalWidth/naturalHeight valent 0.
    await Promise.all(Array.from(allImgs).map((img) => new Promise<void>((res) => {
        if (img.complete) { res(); return; }
        img.onload = () => res();
        img.onerror = () => res();
        setTimeout(res, 1500);
    })));
    // BUG TROUVÉ (2026-09-05, Emeni : « le logo ne s'affiche pas sur iPhone », sur le devis avec le même
    // mécanisme) : le logo est dimensionné via les classes Tailwind max-h-28/max-w-[250px]/object-contain
    // — Safari iOS ne restitue pas cette combinaison de façon fiable dans le <foreignObject> SVG utilisé
    // par html-to-image, le logo disparaît alors silencieusement de l'image capturée. Remplacé par une
    // largeur/hauteur explicites en pixels, calculées depuis les dimensions réelles de l'image.
    const logoImgEl = clone.querySelector<HTMLImageElement>('img[alt="Logo"]');
    if (logoImgEl && logoImgEl.naturalWidth && logoImgEl.naturalHeight) {
        const maxWidth = 250, maxHeight = 112;
        const ratio = logoImgEl.naturalWidth / logoImgEl.naturalHeight;
        let width = maxWidth, height = maxWidth / ratio;
        if (height > maxHeight) { height = maxHeight; width = maxHeight * ratio; }
        logoImgEl.style.width = `${Math.round(width)}px`;
        logoImgEl.style.height = `${Math.round(height)}px`;
        logoImgEl.style.maxWidth = '';
        logoImgEl.style.maxHeight = '';
        logoImgEl.style.objectFit = '';
    }

    try {
        // Attend que les polices soient chargées AVANT de mesurer offsetTop/offsetHeight — sinon la
        // police de repli (plus large/étroite) décale les zones interdites par rapport à la position
        // réelle dans l'image capturée par toPng() juste après (même correctif que le devis Prestation
        // et le contrat d'abonnement).
        if (document.fonts) {
            try { await document.fonts.ready; } catch { /* ignore */ }
        }

        // Zones interdites à la découpe — demande d'Emeni (2026-09-05) : une facture à beaucoup de
        // lignes doit passer sur une deuxième page plutôt que de tout rétrécir sur une seule, sans
        // jamais couper une ligne d'article en deux entre deux pages (même logique que le devis
        // Prestation).
        const cssScale = 210 / clone.scrollWidth;
        const forbiddenElsRaw = [
            ...Array.from(clone.querySelectorAll<HTMLElement>('table')),
            ...Array.from(clone.querySelectorAll<HTMLElement>('tr')),
            ...Array.from(clone.querySelectorAll<HTMLElement>('p')),
            ...Array.from(clone.querySelectorAll<HTMLElement>('li')),
        ];
        const cloneRectTop = clone.getBoundingClientRect().top;
        const MAX_ATOMIC_BLOCK_HEIGHT_MM = 297 - 8 - 8;
        const forbiddenRanges = forbiddenElsRaw.map((el) => {
            const rect = el.getBoundingClientRect();
            return { top: (rect.top - cloneRectTop) * cssScale, bottom: (rect.bottom - cloneRectTop) * cssScale };
        }).filter((r) => (r.bottom - r.top) <= MAX_ATOMIC_BLOCK_HEIGHT_MM);
        const headingKeepWithNext = Array.from(clone.querySelectorAll<HTMLElement>('h1, h2, h3')).map((h) => {
            const rect = h.getBoundingClientRect();
            const top = (rect.top - cloneRectTop) * cssScale;
            let bottom = (rect.bottom - cloneRectTop) * cssScale;
            const next = h.nextElementSibling as HTMLElement | null;
            if (next) {
                const nextRect = next.getBoundingClientRect();
                bottom = Math.max(bottom, (nextRect.top - cloneRectTop) * cssScale + 8);
            }
            return { top, bottom };
        });
        forbiddenRanges.push(...headingKeepWithNext);

        // Ratio de capture plafonné pour rester sous la limite de taille de canvas des navigateurs
        // mobiles (même correctif que contractPdfGenerator.ts / devisPrestationPdfGenerator.ts).
        const safePixelRatio = Math.min(1.5, 4000 / Math.max(clone.scrollWidth, clone.scrollHeight, 1));
        const toPngOpts = {
            cacheBust: true,
            pixelRatio: safePixelRatio,
            canvasWidth: clone.scrollWidth * safePixelRatio,
            canvasHeight: clone.scrollHeight * safePixelRatio,
            backgroundColor: '#ffffff',
            skipFonts: false,
            width: clone.scrollWidth,
            height: clone.scrollHeight,
            quality: 1,
        };
        const imgData = await toPng(clone, toPngOpts);

        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = 210;
        const pageHeight = 297;

        const imageProps = pdf.getImageProperties(imgData);
        // Mise à l'échelle sur la LARGEUR seulement (jamais sur la hauteur, qui compresserait tout sur
        // une seule page), puis découpage vertical page par page — comme le devis.
        const scale = pageWidth / imageProps.width;
        const imageWidth = imageProps.width * scale;
        const imageHeight = imageProps.height * scale;

        // Chaque page utilise une image RECADRÉE à sa tranche exacte (via un canvas), jamais l'image
        // complète repositionnée — sinon jsPDF repeint le contenu entre le point de coupure et le bas
        // de la page précédente une deuxième fois.
        const sourceImg = new Image();
        await new Promise<void>((resolve, reject) => { sourceImg.onload = () => resolve(); sourceImg.onerror = () => reject(new Error('Image de rendu invalide.')); sourceImg.src = imgData; });
        const pxPerMm = imageProps.width / imageWidth;

        // Seule la première page a une marge en haut (incluse dans le contenu capturé, padding:8mm du
        // conteneur) — les pages suivantes démarrent une tranche recadrée en plein milieu du document,
        // sans aucune marge : on réserve donc un blanc de 8mm en haut à partir de la 2e page, et en bas
        // de chaque page qui n'est pas la dernière.
        const CONTINUATION_TOP_MARGIN_MM = 8;
        const BOTTOM_MARGIN_MM = 8;
        let usedHeight = 0;
        let firstPage = true;
        while (usedHeight < imageHeight - 0.5) {
            const pageBudget = (firstPage ? pageHeight : pageHeight - CONTINUATION_TOP_MARGIN_MM) - BOTTOM_MARGIN_MM;
            const naiveBreak = Math.min(usedHeight + pageBudget, imageHeight);
            let breakAt = naiveBreak;
            for (const r of forbiddenRanges) {
                if (naiveBreak > r.top && naiveBreak < r.bottom && r.top > usedHeight + 1 && r.top < breakAt) {
                    breakAt = r.top;
                }
            }
            if (breakAt < naiveBreak) {
                const withMargin = breakAt - 4;
                if (withMargin > usedHeight + 1) breakAt = withMargin;
            }
            if (breakAt <= usedHeight) breakAt = usedHeight + pageBudget; // ligne plus haute qu'une page : coupe quand même, jamais de boucle infinie

            const sliceHeightMm = breakAt - usedHeight;
            const srcY = Math.round(usedHeight * pxPerMm);
            const srcH = Math.max(1, Math.round(sliceHeightMm * pxPerMm));
            const canvas = document.createElement('canvas');
            canvas.width = imageProps.width;
            canvas.height = srcH;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(sourceImg, 0, srcY, imageProps.width, srcH, 0, 0, imageProps.width, srcH);
            const sliceData = canvas.toDataURL('image/png');

            if (!firstPage) pdf.addPage();
            pdf.addImage(sliceData, 'PNG', 0, firstPage ? 0 : CONTINUATION_TOP_MARGIN_MM, imageWidth, sliceHeightMm, undefined, 'SLOW');
            usedHeight = breakAt;
            firstPage = false;
        }

        return pdf;
    } finally {
        document.body.removeChild(wrapper);
    }
};

/**
 * Règles d'impression appliquées à l'écran de création, quand l'utilisateur imprime la page
 * directement (Ctrl+P) plutôt que par le bouton : tout est masqué sauf le document.
 */
export const INVOICE_SCREEN_PRINT_CSS = `
@media print {
    body * { visibility: hidden !important; }
    .invoice-print-area, .invoice-print-area * { visibility: visible !important; }
    .invoice-print-area {
        box-sizing: border-box !important;
        display: flex !important;
        flex-direction: column !important;
        position: absolute !important;
        left: 0 !important;
        right: 0 !important;
        top: 0 !important;
        width: auto !important;
        max-width: none !important;
        box-shadow: none !important;
        border: none !important;
        border-radius: 0 !important;
        overflow: visible !important;
        min-height: calc(297mm - 6mm) !important;
        padding: 3mm !important;
        margin: 0 auto !important;
    }
    .invoice-items-table { page-break-inside: auto; }
    .invoice-scroll { overflow: visible !important; }
    .invoice-items-table { min-width: 0 !important; }
    .invoice-items-table thead { display: table-header-group; }
    .invoice-line-row { break-inside: avoid; page-break-inside: avoid; }
    .invoice-bottom-block { margin-top: auto !important; }
    .invoice-summary-section,
    .invoice-legal-footer,
    .invoice-keep-together,
    .invoice-bottom-block {
        break-inside: avoid-page;
        page-break-inside: avoid;
    }
    .invoice-summary-section { margin-top: 3mm !important; }
    .invoice-legal-footer { margin-top: 3mm !important; padding-top: 2mm !important; }
    .print-hidden { display: none !important; }
}
@page {
    size: A4 portrait;
    margin: 3mm;
}
`;
